import { createRouter, createWebHistory } from 'vue-router'
import { loadServerConfig, isApiConfigured, isAuthenticated, isDevelopment } from '@/api/config'

// Track admin authentication state
let adminAuthenticated = false

export function setAdminAuthenticated(value: boolean) {
  adminAuthenticated = value
}

export function isAdminAuthenticated(): boolean {
  // Check session storage for admin auth token
  if (sessionStorage.getItem('adminAuth')) {
    adminAuthenticated = true
  }
  return adminAuthenticated
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ================== PUBLIC ROUTES ==================
    {
      path: '/',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/verify',
      name: 'verify',
      component: () => import('@/views/VerifyView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/no-tenant',
      name: 'no-tenant',
      component: () => import('@/views/NoTenantView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/setup',
      name: 'setup',
      component: () => import('@/views/SetupView.vue'),
      meta: { requiresAuth: false, devOnly: true }
    },

    // ================== PROTECTED ROUTES ==================
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/invoices',
      name: 'invoices',
      component: () => import('@/views/InvoicesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/pay',
      name: 'pay',
      component: () => import('@/views/PaymentView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/payment-methods',
      name: 'payment-methods',
      component: () => import('@/views/PaymentMethodsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/payments',
      name: 'payments',
      component: () => import('@/views/PaymentsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proofs',
      name: 'proofs',
      component: () => import('@/views/ProofsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proofs/new-ad/:orderImageKey',
      name: 'new-ad-detail',
      component: () => import('@/views/NewAdDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proofs/:proofKey',
      name: 'proof-detail',
      component: () => import('@/views/ProofDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/confirmation',
      name: 'confirmation',
      component: () => import('@/views/ConfirmationView.vue'),
      meta: { requiresAuth: true }
    },

    // ================== ADMIN ROUTES ==================
    {
      path: '/admin',
      name: 'admin-login',
      component: () => import('@/views/admin/AdminLoginView.vue'),
      meta: { requiresAuth: false, isAdmin: true }
    },
    {
      path: '/admin/tenants',
      name: 'admin-tenants',
      component: () => import('@/views/admin/AdminTenantsView.vue'),
      meta: { requiresAuth: false, isAdmin: true, requiresAdminAuth: true }
    },
    {
      path: '/admin/tenants/new',
      name: 'admin-tenant-new',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { requiresAuth: false, isAdmin: true, requiresAdminAuth: true }
    },
    {
      path: '/admin/tenants/:id',
      name: 'admin-tenant-edit',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { requiresAuth: false, isAdmin: true, requiresAdminAuth: true }
    }
  ]
})

// Track if config has been loaded
let configLoaded = false

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  // Admin routes don't need tenant config
  if (to.meta.isAdmin) {
    // Check admin authentication for protected admin routes
    if (to.meta.requiresAdminAuth && !isAdminAuthenticated()) {
      next({ name: 'admin-login' })
      return
    }
    next()
    return
  }

  // Load server config on first navigation (non-admin routes)
  if (!configLoaded) {
    await loadServerConfig()
    configLoaded = true
  }

  // Handle setup page (dev only)
  if (to.name === 'setup') {
    if (!isDevelopment()) {
      next({ name: 'no-tenant' })
      return
    }
    if (isApiConfigured()) {
      next({ name: 'login' })
      return
    }
    next()
    return
  }

  // Check if API is configured
  if (!isApiConfigured() && to.name !== 'no-tenant') {
    if (isDevelopment()) {
      next({ name: 'setup' })
    } else {
      next({ name: 'no-tenant' })
    }
    return
  }

  // Check authentication for protected routes
  if (to.meta.requiresAuth && !isAuthenticated()) {
    console.log(`[Router] Auth required but not authenticated. Redirecting to login. Target: ${to.fullPath}`)
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  // Redirect authenticated users away from login
  if (to.name === 'login' && isAuthenticated()) {
    next({ name: 'dashboard' })
    return
  }

  next()
})

export default router
